import { Injectable } from '@angular/core';
import { ActionBackOfficeResponse } from '../../models/api.model';
import {
  FINANCE_ATTACHMENT_BASE_URL,
  FinanceAttachment,
  FinanceManualChartItem,
  FinanceManualDetail,
  FinanceManualListRow,
  FinanceManualMarketingDashboardDto,
  FinanceManualMarketingDashboardView,
  FinanceManualRecentActivityItem,
  FinanceManualSummary,
  FinanceManualTableDto,
} from '../../models/finance/finance-manual-list.model';

@Injectable({ providedIn: 'root' })
export class MapperFinanceManualListService {
  mapTableFromDto(dto: FinanceManualTableDto): {
    items: FinanceManualListRow[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as FinanceManualTableDto & Record<string, unknown>;
    return {
      items: ((dto.data ?? raw['Data'] ?? []) as FinanceManualListRow[]).map((item) => this.normalizeRow(item)),
      total: Number(dto.total ?? raw['Total'] ?? 0),
      page: Number(dto.page ?? raw['Page'] ?? 1),
      pageSize: Number(dto.pageSize ?? raw['PageSize'] ?? 20),
      totalPages: Number(dto.totalPages ?? raw['TotalPages'] ?? 0),
    };
  }

  mapMarketingDashboard(dto: FinanceManualMarketingDashboardDto): FinanceManualMarketingDashboardView {
    const raw = dto as FinanceManualMarketingDashboardDto & Record<string, unknown>;
    const summary = this.normalizeSummary(dto.summary ?? raw['Summary']);
    const recentRaw = (dto.recentActivity ?? raw['RecentActivity'] ?? []) as FinanceManualRecentActivityItem[];
    const chartRaw = (dto.chartItems ?? raw['ChartItems'] ?? []) as FinanceManualChartItem[];
    const filterRaw = dto.filterOptions ?? (raw['FilterOptions'] as FinanceManualFilterOptions) ?? { feeTypes: [], feeNames: [] };

    return {
      stats: this.mapDashboardStats(summary),
      recentActivity: recentRaw.map((item) => this.normalizeRecent(item)),
      chartItems: chartRaw.map((item) => this.normalizeChartItem(item)),
      filterOptions: {
        feeTypes: filterRaw.feeTypes ?? (filterRaw as unknown as Record<string, unknown>)['FeeTypes'] as string[] ?? [],
        feeNames: filterRaw.feeNames ?? (filterRaw as unknown as Record<string, unknown>)['FeeNames'] as string[] ?? [],
      },
    };
  }

  mapSearchField(type: 'all' | 'receiptId' | 'feeName' | 'description'): string {
    switch (type) {
      case 'receiptId':
        return 'receiptid';
      case 'feeName':
        return 'feename';
      case 'description':
        return 'description';
      default:
        return 'all';
    }
  }

  mapActionResponse(res: unknown) {
    const r = res as ActionBackOfficeResponse & Record<string, unknown>;
    return {
      isSuccess: Boolean(r.isSuccess ?? r['IsSuccess'] ?? false),
      message: String(r.message ?? r['Message'] ?? ''),
      messageEn: String(r.messageEn ?? r['MessageEn'] ?? ''),
      idReturn: Number(r.IdReturn ?? r['idReturn'] ?? r['IdReturn'] ?? 0),
    };
  }

  mapDetailFromDto(dto: unknown): FinanceManualDetail {
    const row = dto as FinanceManualDetail & Record<string, unknown>;
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      receiptId: String(row.receiptId ?? row['ReceiptId'] ?? ''),
      description: String(row.description ?? row['Description'] ?? ''),
      paidDate: (row.paidDate ?? row['PaidDate'] ?? null) as string | null,
      createDate: (row.createDate ?? row['CreateDate'] ?? null) as string | null,
      feeName: String(row.feeName ?? row['FeeName'] ?? ''),
      fee: Number(row.fee ?? row['Fee'] ?? 0),
      vat: Number(row.vat ?? row['Vat'] ?? 0),
      total: Number(row.total ?? row['Total'] ?? 0),
      feeType: String(row.feeType ?? row['FeeType'] ?? ''),
      siteId: Number(row.siteId ?? row['SiteId'] ?? 0),
      siteName: String(row.siteName ?? row['SiteName'] ?? ''),
      invoiceId: (row.invoiceId ?? row['InvoiceId'] ?? null) as number | null,
      invoiceNo: String(row.invoiceNo ?? row['InvoiceNo'] ?? ''),
      bankName: String(row.bankName ?? row['BankName'] ?? ''),
      statusId: String(row.statusId ?? row['StatusId'] ?? ''),
      attachments: this.mapAttachments(row.attachments ?? row['Attachments']),
    };
  }

  mapAttachments(raw: unknown): FinanceAttachment[] {
    if (!Array.isArray(raw)) return [];
    return raw
      .map((item) => {
        const a = item as Record<string, unknown>;
        return {
          id: Number(a['id'] ?? a['Id'] ?? 0),
          purpose: String(a['purpose'] ?? a['Purpose'] ?? ''),
          fileUrl: this.resolveAttachmentUrl(String(a['fileUrl'] ?? a['FileUrl'] ?? '')),
          fileName: String(a['fileName'] ?? a['FileName'] ?? ''),
          contentType: String(a['contentType'] ?? a['ContentType'] ?? ''),
          createDate: (a['createDate'] ?? a['CreateDate'] ?? null) as string | null,
        };
      })
      .filter((a) => !!a.fileUrl);
  }

  private resolveAttachmentUrl(path: string): string {
    const value = path.trim();
    if (!value) return '';
    if (/^https?:\/\//i.test(value)) return value;
    return FINANCE_ATTACHMENT_BASE_URL + value.replace(/^\/+/, '');
  }

  private mapDashboardStats(summary: FinanceManualSummary): FinanceManualMarketingDashboardView['stats'] {
    return [
      {
        title: 'ยอดรวมรายได้',
        subtitle: 'รายได้ทั้งหมดในระบบ',
        value: (summary.totalRevenue || 0).toLocaleString('th-TH') + ' ฿',
        icon: 'payments',
        color: 'text-green-600',
      },
      {
        title: 'จำนวนรายการ',
        subtitle: 'รายการที่ชำระแล้ว',
        value: (summary.totalCount || 0).toLocaleString(),
        icon: 'receipt',
        color: 'text-blue-600',
      },
      {
        title: 'ภาษีมูลค่าเพิ่ม',
        subtitle: 'VAT',
        value: (summary.totalVat || 0).toLocaleString('th-TH') + ' ฿',
        icon: 'account_balance',
        color: 'text-purple-600',
      },
      {
        title: 'เฉลี่ยต่อรายการ',
        subtitle: 'ประมาณการ',
        value: (summary.avgTransaction || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ฿',
        icon: 'analytics',
        color: 'text-orange-600',
      },
    ];
  }

  private normalizeRow(item: unknown): FinanceManualListRow {
    const row = item as FinanceManualListRow & Record<string, unknown>;
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      receiptId: String(row.receiptId ?? row['ReceiptId'] ?? ''),
      description: String(row.description ?? row['Description'] ?? ''),
      paidDate: (row.paidDate ?? row['PaidDate'] ?? null) as string | null,
      feeName: String(row.feeName ?? row['FeeName'] ?? ''),
      fee: Number(row.fee ?? row['Fee'] ?? 0),
      vat: Number(row.vat ?? row['Vat'] ?? 0),
      feeType: String(row.feeType ?? row['FeeType'] ?? ''),
      siteName: String(row.siteName ?? row['SiteName'] ?? ''),
    };
  }

  private normalizeSummary(summary: unknown): FinanceManualSummary {
    const s = summary as FinanceManualSummary & Record<string, unknown>;
    return {
      totalCount: Number(s.totalCount ?? s['TotalCount'] ?? 0),
      newLast7Days: Number(s.newLast7Days ?? s['NewLast7Days'] ?? 0),
      totalRevenue: Number(s.totalRevenue ?? s['TotalRevenue'] ?? 0),
      totalVat: Number(s.totalVat ?? s['TotalVat'] ?? 0),
      avgTransaction: Number(s.avgTransaction ?? s['AvgTransaction'] ?? 0),
    };
  }

  private normalizeRecent(item: unknown): FinanceManualRecentActivityItem {
    const row = item as FinanceManualRecentActivityItem & Record<string, unknown>;
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      siteName: String(row.siteName ?? row['SiteName'] ?? ''),
      feeName: String(row.feeName ?? row['FeeName'] ?? ''),
      fee: Number(row.fee ?? row['Fee'] ?? 0),
      paidDate: (row.paidDate ?? row['PaidDate'] ?? null) as string | null,
    };
  }

  private normalizeChartItem(item: unknown): FinanceManualChartItem {
    const row = item as FinanceManualChartItem & Record<string, unknown>;
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      siteName: String(row.siteName ?? row['SiteName'] ?? ''),
      feeName: String(row.feeName ?? row['FeeName'] ?? ''),
      paidDate: (row.paidDate ?? row['PaidDate'] ?? null) as string | null,
      fee: Number(row.fee ?? row['Fee'] ?? 0),
    };
  }
}

interface FinanceManualFilterOptions {
  feeTypes: string[];
  feeNames: string[];
}
